import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight, ShoppingBag } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountry } from "@/lib/country-context";
import api from "@/lib/api";

// Must match the backend's status pipeline exactly (see backend/models.py Order.status).
const STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  preparing: "Preparing",
  assigned_to_rider: "Rider Assigned",
  in_transit: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  preparing: "bg-orange-100 text-orange-700 border-orange-200",
  assigned_to_rider: "bg-purple-100 text-purple-700 border-purple-200",
  in_transit: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const LIVE_STATUSES = new Set(["pending", "confirmed", "preparing", "assigned_to_rider", "in_transit"]);
const POLL_MS = 15000;

export default function OrdersPage() {
  const { formatPrice } = useCountry();
  const [orders, setOrders] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchOrders = () => {
      Promise.all([api.get("/orders"), api.get("/orders/stats")])
        .then(([o, s]) => {
          if (cancelled) return;
          setOrders(o.data);
          setStats(s.data);
          if (o.data.some((ord) => LIVE_STATUSES.has(ord.status))) {
            timer = setTimeout(fetchOrders, POLL_MS);
          }
        })
        .catch(() => {
          if (!cancelled) { setOrders([]); setStats(null); }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    fetchOrders();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-background border-b p-4 md:p-6">
        <h1 className="font-serif text-2xl font-bold">Your Orders</h1>
      </div>

      <div className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Total Orders" value={String(stats.totalOrders)} testId="stat-total-orders" />
            <StatCard label="Total Spent" value={formatPrice(stats.totalSpent || 0)} testId="stat-total-spent" />
            <StatCard label="This Month" value={String(stats.ordersThisMonth || 0)}
              className="col-span-2 sm:col-span-1" testId="stat-this-month" />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-2xl" />
            ))}
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <ShoppingBag className="h-12 w-12 mb-4 opacity-20" />
            <p className="font-medium">No orders yet</p>
            <p className="text-sm mt-1">Chat with Cravve to place your first order</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-card-${o.id}`}>
                <div className="bg-card border rounded-2xl p-4 hover:shadow-sm transition-all hover:-translate-y-0.5 cursor-pointer group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Package className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{o.restaurant_name}</p>
                        <p className="text-sm text-muted-foreground">{fmtDate(o.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="font-bold">{formatPrice(o.total)}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <p className="text-sm text-muted-foreground">
                      {o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? "s" : ""}
                    </p>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_COLORS[o.status] || "bg-muted text-muted-foreground"}`}>
                      {STATUS_LABELS[o.status] || o.status}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, className = "", testId }) {
  return (
    <div className={`bg-card border rounded-2xl p-4 ${className}`} data-testid={testId}>
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
      <p className="font-serif text-2xl font-bold mt-1 text-primary">{value}</p>
    </div>
  );
}
